import { Check } from 'lucide-react';

interface CheckboxRowProps {
  checked: boolean;
  onToggle: () => void;
  label: string;
  disabled?: boolean;
  /** The block-data key this toggle writes, so an undo can point at the toggle it changed rather
   * than at the whole block. Matches the other field components' `fieldId`. */
  fieldId?: string;
}

/*
 * NO SECOND LINE. The `desc` prop had exactly one caller's string behind it, repeated across
 * seven blocks — "Use when a variable changes the correct answer" — and a hint line that is
 * only useful the first few times you read it is a standing defect here, not a feature: the
 * label is what gets fixed. Removed with the prop so the structure cannot come back by
 * accident, and deliberately not replaced with a `?` popover, which would be a new pattern
 * the app uses nowhere else, hosting the same sentence one click further away.
 */
export function CheckboxRow({ checked, onToggle, label, disabled, fieldId }: CheckboxRowProps) {
  /**
   * A DISABLED TOGGLE ALWAYS READS AS OFF, whatever the stored value says.
   *
   * Settings are deliberately not cleared when a mode takes them away, so switching back restores
   * the teacher's setup intact. The cost of that, before this line, was a greyed-out control still
   * wearing its tick: it claimed the feature was active in a mode that had just taken it away, and
   * offered no way to correct the claim, because the control that would clear it was the disabled
   * one. A teacher in Optional read "Show answer tolerances: on" and could do nothing about it.
   *
   * Forced here rather than asked of each caller, because "pass me the value that accounts for
   * your own disabled condition" is a rule eleven call sites would have to keep and one would
   * eventually drop. The caller passes what it stores; this decides what is shown.
   *
   * This is the DISPLAY half only. A setting that still CHANGES something while disabled is a
   * separate bug in that setting's own readers, and this cannot see them — see
   * `modeGatedSettings` for the behavioural half and the contract test that pairs them.
   */
  const reads = checked && !disabled;
  return (
    <button type='button' role='checkbox' aria-checked={reads} aria-disabled={disabled || undefined}
      data-field-id={fieldId}
      onClick={disabled ? undefined : onToggle}
      className={`flex items-start gap-2.5 text-left ${disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}
    >
      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors ${reads ? 'border-ws-primary bg-ws-primary' : 'border-ws-border bg-ws-btn'}`}>
        {reads && <Check className='h-3.5 w-3.5 text-ws-btn-on-primary' strokeWidth={3} />}
      </span>
      <span className='text-sm text-ws-text'>{label}</span>
    </button>
  );
}
